import { Link } from 'react-router-dom';
import { Spin } from 'antd';
import { RiskScorePill, type WavvestAccountRow } from '@wavvest/wealth-ui';
import './AccountPage.scss';

type AccountPageProps = {
  /** The Wealth row for this account; undefined until accounts load or when unknown. */
  row?: WavvestAccountRow;
  accountNumber?: string;
  loading: boolean;
};

const lastFour = (value?: string) => {
  const trimmed = value?.trim();
  return trimmed && trimmed.length >= 4 ? trimmed.slice(-4) : undefined;
};

/**
 * One Wavvest account (design board Account): breadcrumb back to Wealth, the
 * account's name, type and risk, its transfer actions and funding bank, then
 * cost basis, allocation, holdings and transfers.
 */
const AccountPage = ({ row, accountNumber, loading }: AccountPageProps) => {
  const breadcrumb = (current: string) => (
    <nav className="account-page-breadcrumb" aria-label="Breadcrumb">
      <Link to="/home/wealth">Wealth</Link>
      <span aria-hidden="true">/</span>
      <span aria-current="page">{current}</span>
    </nav>
  );

  if (!row) {
    return (
      <div className="account-page">
        {breadcrumb('Account')}
        {loading ? (
          <div className="account-page-loading">
            <Spin />
          </div>
        ) : (
          <p className="account-page-missing">
            We couldn&apos;t find this account. <Link to="/home/wealth">Back to Wealth</Link>
          </p>
        )}
      </div>
    );
  }

  // Accounts without a nickname are titled by their number already.
  const last4 = accountNumber?.trim() === row.title ? undefined : lastFour(accountNumber);
  return (
    <div className="account-page">
      {breadcrumb(row.title)}
      <header className="account-page-header">
        <div className="account-page-identity">
          <h1>{row.title}</h1>
          <div className="account-page-meta">
            {row.typeLabel ? <span>{row.typeLabel}</span> : null}
            {last4 ? <span>••{last4}</span> : null}
            {row.risk ? <RiskScorePill risk={row.risk} /> : null}
          </div>
        </div>
        <div className="account-page-balance">
          <span>Balance</span>
          <strong>{row.balance}</strong>
        </div>
      </header>
      <div className="account-page-toolbar">
        {row.funding ? (
          <div className="account-page-funding">
            <span>Funding source</span>
            {row.funding}
          </div>
        ) : (
          <span />
        )}
        {row.actions ? <div className="account-page-actions">{row.actions}</div> : null}
      </div>
      <section className="account-page-details" aria-label="Account details">
        {row.details}
      </section>
    </div>
  );
};

export default AccountPage;
